@let messages = store.messages();
@let busy = store.busy();
@let stage = store.stage();

<section class="ai-chat-panel" [attr.aria-label]="'document.edit.aiEdit.panelLabel' | translate">
    <ra-chat-message-list class="chat-list">
        @if (!messages.length && !busy && store.restoring()) {
            <!-- A conversa anterior está vindo: esqueleto no lugar do convite, para a tela
                 não piscar o chat vazio antes das falas. -->
            <div class="restoring" aria-busy="true">
                <ra-skeleton width="55%" [height]="14" />
                <ra-skeleton width="80%" [height]="14" />
                <ra-skeleton width="40%" [height]="14" />
            </div>
        } @else if (!messages.length && !busy) {
            <!-- Chat vazio: o convite. Os chips de pedido ficam acima do composer. -->
            <div class="empty-state">
                <ra-chat-orb [size]="48" [withShadow]="true" />
                <h2 class="empty-title">{{ 'document.edit.aiEdit.emptyTitle' | translate }}</h2>
                <p class="empty-subtitle">{{ 'document.edit.aiEdit.emptySubtitle' | translate }}</p>
            </div>
        } @else {
            <div class="editing-divider">
                <span class="divider-line"></span>
                <span class="divider-text">
                    {{ 'document.edit.aiEdit.editing' | translate }} <strong>{{ title() }}</strong>
                </span>
                <span class="divider-line"></span>
            </div>
        }

        @for (message of messages; track message.id) {
            @if (message.role === 'user') {
                <!-- Estado 1: a bolha aparece na hora, com "Enviando" discreto embaixo. -->
                <div class="user-turn">
                    <ra-chat-bubble sender="user">
                        @if (message.media?.length) {
                            <div class="bubble-media" [class.single]="message.media!.length === 1">
                                @for (media of message.media; track media.url) {
                                    @if (media.kind === 'video') {
                                        <span class="bubble-media__item video">
                                            @if (media.thumbUrl) {
                                                <img [src]="media.thumbUrl" alt="" />
                                            }
                                            <ra-icon class="bubble-media__play" icon="video-play" varColor="contentInverse" [size]="20" />
                                        </span>
                                    } @else {
                                        <img class="bubble-media__item" [src]="media.url" alt="" />
                                    }
                                }
                            </div>
                        }
                        @if (message.text) {
                            <p>{{ message.text }}</p>
                        }
                    </ra-chat-bubble>
                    @if (message.sending) {
                        <span class="sending" role="status">
                            <span class="mini-spinner" aria-hidden="true"></span>
                            {{ 'document.edit.aiEdit.sending' | translate }}
                        </span>
                    }
                </div>
            } @else {
                <ra-chat-bubble sender="assistant">
                    <div class="assistant-turn">
                        <ra-chat-orb [size]="28" />

                        <div class="assistant-body">
                            @if (message.error) {
                                <div class="error-card" role="alert">
                                    <strong>{{ 'document.edit.aiEdit.errorTitle' | translate }}</strong>
                                    <p>{{ message.text | translate }}</p>
                                    @if (message.retryText) {
                                        <div class="card-actions">
                                            <button type="button" class="mini-button primary" [disabled]="busy" (click)="store.retry(message)">
                                                <ra-icon icon="rotate-right" varColor="currentColor" [size]="14" />
                                                {{ 'document.edit.aiEdit.retry' | translate }}
                                            </button>
                                        </div>
                                    }
                                </div>
                            } @else {
                                <!-- Estado 4: resposta curta, o checklist verde e os próximos passos. -->
                                <p>{{ message.text }}</p>
                                @if (message.webSearched) {
                                    <span class="web-searched">
                                        <ra-icon icon="global" varColor="currentColor" [size]="12" />
                                        {{ 'document.edit.aiEdit.webSearched' | translate }}
                                    </span>
                                }

                                @if (message.changes?.length) {
                                    <ai-edit-checklist [changes]="message.changes!" />
                                }

                                <!-- Estados 5 e 6: cada conflito e cada falha vira um cartão com a decisão. -->
                                @for (change of attention(message); track change.id) {
                                    @if (change.status === 'conflict') {
                                        @let conflict = change.conflict;
                                        @let editor = conflict?.userName || '';
                                        @let settling = store.settling() === change.id;
                                        <div class="decision-card decision-card--conflict" role="group" [attr.aria-label]="'document.edit.aiEdit.conflictLabel' | translate">
                                            <div class="decision-head">
                                                @if (conflict?.userAvatar) {
                                                    <img class="decision-avatar" [src]="conflict!.userAvatar" alt="" />
                                                } @else {
                                                    <span class="decision-avatar decision-avatar--initial" aria-hidden="true">{{ initial(editor) }}</span>
                                                }
                                                <div class="decision-title">
                                                    <strong>{{ (editor ? 'document.edit.aiEdit.conflictTitle' : 'document.edit.aiEdit.conflictTitleUnknown') | translate: { name: editor } }}</strong>
                                                    <span class="decision-where">{{ change.label }}@if (change.where) { · {{ change.where }}}</span>
                                                </div>
                                            </div>
                                            <p class="decision-hint">{{ 'document.edit.aiEdit.conflictHint' | translate }}</p>
                                            <div class="decision-versions" [class.expanded]="expanded().has(change.id)">
                                                <div class="version version--ai">
                                                    <span class="version-label">{{ 'document.edit.aiEdit.conflictAiVersion' | translate }}</span>
                                                    <p class="version-text">{{ conflict?.aiPreview || ('document.edit.aiEdit.conflictNoPreview' | translate) }}</p>
                                                </div>
                                                <div class="version version--human">
                                                    <span class="version-label">{{ (editor ? 'document.edit.aiEdit.conflictHumanVersion' : 'document.edit.aiEdit.conflictHumanVersionUnknown') | translate: { name: editor } }}</span>
                                                    <p class="version-text">{{ conflict?.humanPreview || ('document.edit.aiEdit.conflictNoPreview' | translate) }}</p>
                                                </div>
                                            </div>
                                            <div class="card-actions">
                                                <button type="button" class="mini-button primary" [disabled]="busy || !!store.settling()" (click)="store.resolveConflict(message, change, 'ai')">
                                                    {{ (settling ? 'document.edit.aiEdit.resolving' : 'document.edit.aiEdit.useAi') | translate }}
                                                </button>
                                                <button type="button" class="mini-button" [disabled]="busy || !!store.settling()" (click)="store.resolveConflict(message, change, 'human')">
                                                    {{ (editor ? 'document.edit.aiEdit.keepHuman' : 'document.edit.aiEdit.keepHumanUnknown') | translate: { name: editor } }}
                                                </button>
                                                <button type="button" class="mini-button ghost" (click)="toggleExpanded(change.id)">
                                                    {{ (expanded().has(change.id) ? 'document.edit.aiEdit.showLess' : 'document.edit.aiEdit.showBoth') | translate }}
                                                </button>
                                            </div>
                                        </div>
                                    } @else {
                                        <div class="decision-card decision-card--error" role="alert">
                                            <strong>{{ 'document.edit.aiEdit.changeFailedTitle' | translate: { label: change.label } }}</strong>
                                            <p>{{ change.error || ('document.edit.aiEdit.changeFailedDefault' | translate) }} {{ 'document.edit.aiEdit.changeFailedHint' | translate }}</p>
                                            <div class="card-actions">
                                                <button type="button" class="mini-button primary" [disabled]="busy || !!store.settling()" (click)="store.retryChange(message, change)">
                                                    <ra-icon icon="rotate-right" varColor="currentColor" [size]="14" />
                                                    {{ (store.settling() === change.id ? 'document.edit.aiEdit.retryingChange' : 'document.edit.aiEdit.retryChange') | translate }}
                                                </button>
                                            </div>
                                        </div>
                                    }
                                }

                                @if (message.reverted) {
                                    <ra-badge class="reverted-badge" variant="neutral" size="small" [label]="'document.edit.aiEdit.reverted' | translate" />
                                } @else if (message.taskId && message.taskId === store.lastUndoableTaskId()) {
                                    <div class="chips">
                                        @for (suggestion of message.suggestions ?? []; track suggestion) {
                                            <button type="button" class="chip" [disabled]="busy" (click)="store.send(suggestion)">{{ suggestion }}</button>
                                        }
                                        <button type="button" class="chip" [disabled]="busy || store.reverting()" (click)="store.undo(message.taskId)">
                                            {{ (store.reverting() ? 'document.edit.aiEdit.undoing' : 'document.edit.aiEdit.undoAll') | translate }}
                                        </button>
                                    </div>
                                }
                            }
                        </div>
                    </div>
                </ra-chat-bubble>
            }
        }

        <!-- Pedido em andamento -->
        @if (stage === 'thinking') {
            <!-- Estado 2: o orb pulsa e o texto brilhante diz a etapa. -->
            <ra-chat-bubble sender="assistant">
                <div class="assistant-turn" role="status" aria-live="polite">
                    <ra-chat-orb [size]="28" [pulse]="true" />
                    <div class="assistant-body thinking">
                        <!-- O texto muda com o que a IA está olhando; cada troca entra com fade. -->
                        @let stageLabel = store.stageText() || ('document.edit.aiEdit.thinkingDefault' | translate);
                        <div class="stage-row">
                            @for (label of [stageLabel]; track label) {
                                <ra-shiny-text class="stage-text" animate.enter="stage-in" [speed]="1.6">{{ label }}</ra-shiny-text>
                            }
                            @if (store.elapsedText()) {
                                <span class="elapsed" aria-hidden="true">{{ 'document.edit.aiEdit.working' | translate: { time: store.elapsedText() } }}</span>
                            }
                        </div>
                        <ra-skeleton width="70%" [height]="10" />
                        <ra-skeleton width="45%" [height]="10" />
                    </div>
                </div>
            </ra-chat-bubble>
        } @else if (stage === 'applying') {
            <!-- Estado 3: o checklist anda (pendente → girando → check). -->
            @let total = store.changes().length;
            <ra-chat-bubble sender="assistant">
                <div class="assistant-turn">
                    <ra-chat-orb [size]="28" [pulse]="true" />
                    <div class="assistant-body">
                        <p class="stage-row">
                            <span>{{ (total === 1 ? 'document.edit.aiEdit.planIntroOne' : 'document.edit.aiEdit.planIntro') | translate: { count: total } }}</span>
                            @if (store.elapsedText()) {
                                <span class="elapsed" aria-hidden="true">{{ 'document.edit.aiEdit.working' | translate: { time: store.elapsedText() } }}</span>
                            }
                        </p>
                        @if (store.cancelling()) {
                            <ra-shiny-text class="stage-text" [speed]="1.6">{{ 'document.edit.aiEdit.cancelling' | translate }}</ra-shiny-text>
                        }
                        @if (total) {
                            <ai-edit-checklist [changes]="store.changes()" />
                        }
                    </div>
                </div>
            </ra-chat-bubble>
        }
    </ra-chat-message-list>

    <footer class="chat-footer">
        @if (showStarters()) {
            <div class="chips starters">
                @for (key of starterKeys; track key) {
                    @let text = key | translate;
                    <button type="button" class="chip" (click)="sendStarter(text)">{{ text }}</button>
                }
            </div>
        }

        <ra-chat-composer
            [value]="store.draft()"
            (valueChange)="store.draft.set($event)"
            [disabled]="busy"
            [stoppable]="busy && !!store.currentRun()?.taskId"
            [stopping]="store.cancelling()"
            [stopLabel]="'document.edit.aiEdit.stop' | translate"
            (stop)="store.cancel()"
            [canSend]="!store.uploading()"
            [allowEmpty]="hasReadyAttachment()"
            [placeholder]="'document.edit.aiEdit.composerPlaceholder' | translate"
            [hint]="(store.uploading() ? 'document.edit.aiEdit.uploadingHint' : 'document.edit.aiEdit.composerHint') | translate"
            [sendLabel]="'document.edit.aiEdit.send' | translate"
            (send)="store.send($event)">
            <!-- Anexos do próximo pedido, dentro da caixa (como no Claude). -->
            @if (store.attachments().length) {
                <div raChatComposerTop class="attachments" (click)="$event.stopPropagation()">
                    @for (attachment of store.attachments(); track attachment.id) {
                        <div
                            class="attachment"
                            [class.uploading]="attachment.status === 'uploading'"
                            [class.failed]="attachment.status === 'error'"
                            [attr.title]="attachment.name">
                            @if (attachment.kind === 'video') {
                                <video [src]="attachment.previewUrl" muted preload="metadata"></video>
                                <ra-icon class="attachment__play" icon="video-play" varColor="contentInverse" [size]="16" />
                            } @else {
                                <img [src]="attachment.previewUrl" alt="" />
                            }
                            @if (attachment.status === 'uploading') {
                                <span class="attachment__progress" role="progressbar" [attr.aria-valuenow]="attachment.progress">
                                    <span [style.width.%]="attachment.progress"></span>
                                </span>
                            } @else if (attachment.status === 'error') {
                                <span class="attachment__error">{{ 'document.edit.aiEdit.uploadFailed' | translate }}</span>
                            }
                            <button
                                type="button"
                                class="attachment__remove"
                                [attr.aria-label]="'document.edit.aiEdit.removeAttachment' | translate"
                                (click)="store.removeAttachment(attachment.id)">
                                <ra-icon icon="close" varColor="currentColor" [size]="8" />
                            </button>
                        </div>
                    }
                </div>
            }

            <button
                raChatComposerStart
                type="button"
                class="attach-button"
                [disabled]="busy"
                [attr.aria-label]="'document.edit.aiEdit.attach' | translate"
                [title]="'document.edit.aiEdit.attach' | translate"
                (click)="$event.stopPropagation(); openFilePicker()">
                <ra-icon icon="gallery" varColor="currentColor" [size]="18" />
            </button>
        </ra-chat-composer>
        <input #fileInput type="file" accept="image/*,video/*" multiple hidden (change)="onFilesPicked($event)" />

        <p class="footnote" aria-live="polite">
            {{ (busy ? 'document.edit.aiEdit.footnoteBusy' : 'document.edit.aiEdit.footnoteIdle') | translate }}
        </p>
    </footer>

    <!-- Arrastando fotos ou vídeos por cima do chat: a área inteira vira o lugar de soltar. -->
    @if (dragging()) {
        <div class="drop-zone" aria-hidden="true">
            <div class="drop-zone__card">
                <ra-icon icon="gallery" varColor="primary" [size]="28" />
                <strong>{{ 'document.edit.aiEdit.dropTitle' | translate }}</strong>
                <span>{{ 'document.edit.aiEdit.dropHint' | translate }}</span>
            </div>
        </div>
    }
</section>
